/**
 * Grafico a barre/linea per incassi mensili/annuali.
 * Nessuna dipendenza esterna: SVG inline.
 */
export default function RevenueChart({ data, period = 'month' }) {
  if (!data?.length) {
    return (
      <div className="card-elevated p-6 text-center text-surface-4">
        Nessun dato disponibile per il periodo selezionato
      </div>
    );
  }

  // Trova max per scalare
  const maxRevenue = Math.max(...data.map((d) => d.revenue), 1);
  const maxCount = Math.max(...data.map((d) => d.cnt), 1);

  const barWidth = 32;
  const gap = 8;
  const chartHeight = 200;
  const chartWidth = data.length * (barWidth + gap) + gap;
  const paddingLeft = 50;
  const paddingBottom = 40;

  // Formatta label asse X
  const formatLabel = (p) => {
    if (period === 'year') return p;
    const [y, m] = p.split('-');
    return `${m}/${y.slice(-2)}`;
  };

  // Formatta valore Y
  const formatValue = (v) => {
    if (v >= 1000000) return (v / 1000000).toFixed(1) + 'M';
    if (v >= 1000) return (v / 1000).toFixed(1) + 'k';
    return v.toFixed(0);
  };

  return (
    <div className="card-elevated p-4">
      <h3 className="text-sm font-medium text-surface-3 uppercase tracking-wide mb-4">
        Andamento incassi {period === 'year' ? '(annuale)' : '(mensile)'}
      </h3>

      <div className="flex items-end gap-2 h-56 overflow-x-auto pb-2" style={{ width: `${chartWidth + paddingLeft}px` }}>
        {/* Assi Y */}
        <svg width={paddingLeft} height={chartHeight + 20} className="flex-shrink-0" aria-hidden="true">
          <line x1={paddingLeft - 5} y1={10} x2={paddingLeft - 5} y2={chartHeight + 10} stroke="var(--color-line)" strokeWidth="1" />
          {[0, 0.25, 0.5, 0.75, 1].forEach((t) => {
            const y = 10 + (1 - t) * chartHeight;
            const val = formatValue(maxRevenue * t);
          })}
        </svg>

        {/* Barre */}
        <svg width={chartWidth} height={chartHeight + 20} className="flex-shrink-0" role="img" aria-label="Grafico incassi">
          {data.map((d, i) => {
            const x = i * (barWidth + gap) + gap;
            const barHeight = (d.revenue / maxRevenue) * chartHeight;
            const y = 10 + chartHeight - barHeight;

            return (
              <g key={d.p} className="cursor-pointer group">
                <rect
                  x={x}
                  y={y}
                  width={barWidth}
                  height={barHeight}
                  rx={4}
                  fill="var(--color-brand-500)"
                  opacity={0.8}
                  transition="opacity 0.2s"
                  className="group-hover:opacity-100"
                />
                <title>{formatLabel(d.p)}: €{d.revenue.toLocaleString('it-IT')} ({d.cnt} preventivi)</title>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Label asse X */}
      <div className="flex gap-2 mt-1 overflow-x-auto" style={{ width: `${chartWidth + paddingLeft}px` }}>
        <div style={{ width: `${paddingLeft}px` }} className="flex-shrink-0" />
        {data.map((d, i) => (
          <div key={d.p} className="flex-shrink-0 text-center text-xs text-surface-4" style={{ width: `${barWidth + gap}px`, marginLeft: i === 0 ? `${gap}px` : 0 }}>
            {formatLabel(d.p)}
          </div>
        ))}
      </div>

      {/* Legenda valori */}
      <div className="flex justify-between text-xs text-surface-4 mt-2">
        <span>0</span>
        <span>{formatValue(maxRevenue / 2)}</span>
        <span>{formatValue(maxRevenue)}</span>
      </div>
    </div>
  );
}